"use client";

import { useCallback } from "react";
import {
  clearAllDismissedCaches,
  clearDismissedCache,
} from "@/hooks/useDismissedChecks";
import { apiUrl } from "@/lib/aicheck/api-base";
import {
  clearAllAICheckCaches,
  getCachedProjects as getCachedProjectsFromInventory,
  getProjectCacheEntry as getProjectCacheEntryFromInventory,
  getRecentCacheEvictions as getRecentCacheEvictionsFromInventory,
  getHistory as getRunHistory,
  removeProjectCacheBundle,
  removeProjectHistoryEntries,
  removeProjectLanguageHistoryEntry,
  removeProjectSelectionKeys,
  syncProjectCacheInventory,
  upsertHistoryEntry,
} from "@/lib/aicheck/cache-inventory";
import { getGraphExpandedKey } from "@/lib/aicheck/cache-keys";
import {
  isExternalProjectDataSource,
  isSameProjectDataSource,
} from "@/lib/aicheck/project-data-source";
import { projectTitleFromRunResult } from "@/lib/aicheck/project-title";
import { authService } from "@/services/auth";
import type {
  AICheckRunHistoryEntry,
  AICheckRunResult,
  CacheGetResult,
  ContactTopicsResult,
  OutputLanguage,
  ProjectDataSourceDescriptor,
} from "@/types/aicheck";

// --- In-memory result cache (server is the source of truth) ---
//
// Run results are persisted server-side (/api/aicheck/projects/.../latest);
// the browser keeps only a per-tab in-memory copy of what it is displaying.
// Nothing result-sized is written to localStorage anymore.

const memoryResults = new Map<string, AICheckRunResult>();
const memoryContactTopics = new Map<number, ContactTopicsResult>();

function resultMemoryKey(projectId: number, language: OutputLanguage): string {
  return `${projectId}:${language}`;
}

/**
 * Fetch the latest completed run for a project from server history and prime
 * the in-memory cache. Returns null when no server-side run exists.
 */
export async function fetchServerResult(
  projectId: number,
  language: OutputLanguage,
): Promise<AICheckRunResult | null> {
  try {
    const token = await authService.getIdToken();
    if (!token) throw new Error("未認証です。再度サインインしてください。");
    const res = await fetch(
      apiUrl(
        `/api/aicheck/projects/${encodeURIComponent(String(projectId))}/latest?language=${language}`,
      ),
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) return null;
    const body = (await res.json()) as {
      data?: AICheckRunResult;
      run?: { runId?: string };
    };
    const result = body.data ?? null;
    if (result) {
      // /latest は run(runId) と data(result本体) を分けて返す。runId を result に
      // 載せ、run 単位の付随機能（指摘要約など）が参照できるようにする。
      if (body.run?.runId && !result.runId) {
        result.runId = body.run.runId;
      }
      memoryResults.set(resultMemoryKey(projectId, language), result);
      if (result.contactTopicsResult) {
        memoryContactTopics.set(projectId, result.contactTopicsResult);
      }
    }
    return result;
  } catch (e) {
    console.warn(
      `[history] Failed to fetch server result for project ${projectId}:`,
      e,
    );
    return null;
  }
}

function buildHistoryEntry(result: AICheckRunResult): AICheckRunHistoryEntry {
  const source = result.projectDataSource;
  return {
    projectId: result.projectId,
    projectTitle: projectTitleFromRunResult(result),
    runAt: result.completedAt,
    status: result.status,
    language: result.language,
    sourceLabel: source?.label,
    sourceKind: source?.kind,
    sourceCacheKey: source?.origin?.cacheKey,
    usesExternalProjectData: isExternalProjectDataSource(source),
    aiResultCount: result.aiResults.length,
    consistencyResultCount: result.consistencyResults.length,
    passCount:
      result.aiResults.filter((r) => r.status === "pass").length +
      result.consistencyResults.filter((r) => r.passed).length,
    warningCount:
      result.aiResults.filter((r) => r.status === "warning").length +
      result.consistencyResults.filter(
        (r) => !r.passed && r.severity === "warning",
      ).length,
    failCount:
      result.aiResults.filter(
        (r) => r.status === "fail" || r.status === "error",
      ).length +
      result.consistencyResults.filter(
        (r) => !r.passed && r.severity === "error",
      ).length,
  };
}

export function findCachedResultByProjectDataSource(
  source: ProjectDataSourceDescriptor | null | undefined,
  language?: OutputLanguage,
): AICheckRunResult | null {
  if (!source) return null;
  const matches: AICheckRunResult[] = [];
  for (const data of memoryResults.values()) {
    if (language && data.language !== language) continue;
    if (isSameProjectDataSource(data.projectDataSource, source)) {
      matches.push(data);
    }
  }
  return (
    matches.sort((a, b) =>
      (b.completedAt || b.startedAt).localeCompare(
        a.completedAt || a.startedAt,
      ),
    )[0] ?? null
  );
}

export function useLocalResults() {
  // Keep the displayed result in tab memory only. The durable copy is written
  // server-side when the run completes; history metadata (tiny) stays in
  // localStorage to drive the project-ID datalist.
  const saveResult = useCallback((result: AICheckRunResult) => {
    memoryResults.set(
      resultMemoryKey(result.projectId, result.language),
      result,
    );
    try {
      upsertHistoryEntry(buildHistoryEntry(result));
    } catch (e) {
      console.warn(
        `[cache] Failed to record history for project ${result.projectId}:`,
        e,
      );
    }
  }, []);

  const getResult = useCallback(
    (projectId: number, lang: OutputLanguage): CacheGetResult => {
      const result =
        memoryResults.get(resultMemoryKey(projectId, lang)) ?? null;
      return { result, staleReason: null };
    },
    [],
  );

  const fetchResultFromServer = useCallback(
    (projectId: number, lang: OutputLanguage) =>
      fetchServerResult(projectId, lang),
    [],
  );

  const findResultByProjectDataSource = useCallback(
    (
      source: ProjectDataSourceDescriptor | null | undefined,
      language?: OutputLanguage,
    ) => findCachedResultByProjectDataSource(source, language),
    [],
  );

  const clearResult = useCallback((projectId: number, lang: OutputLanguage) => {
    memoryResults.delete(resultMemoryKey(projectId, lang));
    removeProjectSelectionKeys(projectId, {
      results: true,
      checkpoints: true,
      languages: [lang],
    });
    removeProjectLanguageHistoryEntry(projectId, lang);
    syncProjectCacheInventory();
  }, []);

  const clearAllResults = useCallback((projectId: number) => {
    for (const lang of ["ja", "en"] as OutputLanguage[]) {
      memoryResults.delete(resultMemoryKey(projectId, lang));
    }
    removeProjectSelectionKeys(projectId, {
      results: true,
      checkpoints: true,
      graphExpanded: true,
    });
    removeProjectHistoryEntries(projectId);
    syncProjectCacheInventory();
  }, []);

  const clearContactTopics = useCallback((projectId: number) => {
    memoryContactTopics.delete(projectId);
    removeProjectSelectionKeys(projectId, { contactTopics: true });
  }, []);

  const clearAllCaches = useCallback((projectId: number) => {
    for (const lang of ["ja", "en"] as OutputLanguage[]) {
      memoryResults.delete(resultMemoryKey(projectId, lang));
    }
    memoryContactTopics.delete(projectId);
    clearDismissedCache(projectId);
    removeProjectCacheBundle(projectId);
    syncProjectCacheInventory();
  }, []);

  const clearSelectedCaches = useCallback(
    (
      projectId: number,
      options: {
        results?: boolean;
        contactTopics?: boolean;
        dismissed?: boolean;
        projectDataSources?: boolean;
        mergeCheckResults?: boolean;
        freeformReviewResults?: boolean;
        languages?: OutputLanguage[];
      },
    ) => {
      if (options.dismissed) {
        clearDismissedCache(projectId);
      }
      if (options.results) {
        if (options.languages && options.languages.length > 0) {
          for (const language of [...new Set(options.languages)]) {
            removeProjectLanguageHistoryEntry(projectId, language);
          }
        } else {
          removeProjectHistoryEntries(projectId);
        }
      }
      removeProjectSelectionKeys(projectId, {
        results: options.results,
        checkpoints: options.results,
        contactTopics: options.contactTopics,
        dismissed: false,
        projectDataSources: options.projectDataSources,
        mergeCheckResults: options.mergeCheckResults,
        freeformReviewResults: options.freeformReviewResults,
        graphExpanded:
          options.results &&
          (!options.languages || options.languages.length === 0),
        languages: options.languages,
      });
    },
    [],
  );

  const clearProjectCacheBundle = useCallback((projectId: number) => {
    for (const lang of ["ja", "en"] as OutputLanguage[]) {
      memoryResults.delete(resultMemoryKey(projectId, lang));
    }
    memoryContactTopics.delete(projectId);
    clearDismissedCache(projectId);
    removeProjectCacheBundle(projectId);
  }, []);

  const clearAllProjectCaches = useCallback(() => {
    memoryResults.clear();
    memoryContactTopics.clear();
    clearAllDismissedCaches();
    clearAllAICheckCaches();
  }, []);

  const getContactTopics = useCallback(
    (projectId: number): ContactTopicsResult | null =>
      memoryContactTopics.get(projectId) ?? null,
    [],
  );

  const getGraphExpanded = useCallback((projectId: number): boolean => {
    try {
      return localStorage.getItem(getGraphExpandedKey(projectId)) === "1";
    } catch {
      return false;
    }
  }, []);

  const setGraphExpanded = useCallback(
    (projectId: number, expanded: boolean) => {
      try {
        if (expanded) localStorage.setItem(getGraphExpandedKey(projectId), "1");
        else localStorage.removeItem(getGraphExpandedKey(projectId));
        syncProjectCacheInventory();
      } catch {
        /* ignore */
      }
    },
    [],
  );

  const saveContactTopics = useCallback(
    (projectId: number, result: ContactTopicsResult) => {
      memoryContactTopics.set(projectId, result);
    },
    [],
  );

  const getCachedProjects = useCallback(
    (currentProjectId?: number | null) =>
      getCachedProjectsFromInventory({ currentProjectId }),
    [],
  );

  const getProjectCacheEntry = useCallback(
    (projectId: number) => getProjectCacheEntryFromInventory(projectId),
    [],
  );

  const getRecentCacheEvictions = useCallback(
    () => getRecentCacheEvictionsFromInventory(),
    [],
  );

  return {
    saveResult,
    getResult,
    fetchResultFromServer,
    findResultByProjectDataSource,
    clearResult,
    clearAllResults,
    clearContactTopics,
    clearAllCaches,
    clearSelectedCaches,
    clearProjectCacheBundle,
    clearAllProjectCaches,
    getHistory: getRunHistory,
    getCachedProjects,
    getProjectCacheEntry,
    getRecentCacheEvictions,
    getContactTopics,
    saveContactTopics,
    getGraphExpanded,
    setGraphExpanded,
  };
}
